import { defineStore } from "pinia";
import { fetchUserPrivilegeAssignments, resolveKeyonPrivilegeIdentity } from "@/api/privilegesApi";

export const usePrivilegesStore = defineStore("privileges", {
  state: () => ({
    /** Rows from GetPrivilegeAssignmentsForSubject (includes id, effective_granted, user_override, …) */
    assignments: [],
    loading: false,
    error: null,
    /** `${accountnumber}:${unique_ID}` for last successful load */
    loadedKey: "",
    /**
     * The in-flight load, so concurrent callers join it instead of racing it.
     * A promise, not data — nothing reads it but `fetchForCurrentSession`.
     */
    inflight: null,
  }),

  getters: {
    /**
     * Whether the privilege is effectively allowed for the current user.
     * Usage: `const allowed = store.effectiveGranted(privilegeId)`
     */
    effectiveGranted: (state) => (privilegeId) => {
      const id = Number(privilegeId);
      if (!Number.isFinite(id) || id <= 0) return false;
      for (const row of state.assignments) {
        if (Number(row && row.id) === id) {
          return !!row.effective_granted;
        }
      }
      return false;
    },

    /** Full row for a privilege id, or null */
    rowById: (state) => (privilegeId) => {
      const id = Number(privilegeId);
      if (!Number.isFinite(id) || id <= 0) return null;
      for (const row of state.assignments) {
        if (Number(row && row.id) === id) {
          return row;
        }
      }
      return null;
    },
  },

  actions: {
    clear() {
      this.assignments = [];
      this.error = null;
      this.loadedKey = "";
      this.loading = false;
      this.inflight = null;
    },

    /**
     * Load privileges for the given session snapshot (Vuex login/getAllData shape).
     *
     * A hard reload calls this from three places at once — main.js on mount,
     * the `login/LoggedIn` subscriber, and `updateApplicationModel` — so an
     * awaiting caller is normally the second or third to arrive. It used to
     * bail on `if (this.loading) return`, resolving IMMEDIATELY with
     * `assignments: []` and `loadedKey: ""` while the first call was still in
     * flight. Callers cannot tell that from a finished load, and the directives
     * pages read it as "this user holds nothing", failed closed, and froze
     * there — the store answered a question it had not asked yet.
     *
     * Concurrent callers now join the in-flight load instead.
     */
    async fetchForCurrentSession(phrData) {
      const { accountnumber, unique_ID } = resolveKeyonPrivilegeIdentity(phrData);
      const key = `${accountnumber}:${unique_ID}`;

      if (!accountnumber || !unique_ID) {
        this.assignments = [];
        this.error = "missing_identity";
        this.loadedKey = "";
        return;
      }

      if (this.inflight) {
        await this.inflight;
        // Same session: that load answered for us too. A different one (an
        // impersonation switch mid-flight) still needs its own request.
        if (this.loadedKey === key) return;
      }

      const run = this.loadAssignments(phrData, key);
      this.inflight = run;
      try {
        await run;
      } finally {
        if (this.inflight === run) this.inflight = null;
      }
    },

    /**
     * The request itself. Split out so `fetchForCurrentSession` can hand the
     * same promise to every concurrent caller; never call this directly.
     */
    async loadAssignments(phrData, key) {
      this.loading = true;
      this.error = null;

      try {
        const result = await fetchUserPrivilegeAssignments(phrData);
        if (!result.ok) {
          this.assignments = [];
          this.error = result.error === "missing_identity" ? "missing_identity" : "load_failed";
          this.loadedKey = "";
          return;
        }
        this.assignments = result.data;
        console.log("[privileges] loaded assignments:", this.assignments);
        this.loadedKey = key;
      } catch (e) {
        this.assignments = [];
        this.error = e && e.message ? e.message : "load_failed";
        this.loadedKey = "";
      } finally {
        this.loading = false;
      }
    },
  },
});
